type CacheEntry<T> = {
  data?: T;
  request?: Promise<T>;
  timeoutId?: ReturnType<typeof setTimeout>; // for keepUntil
};

type FacadeCache = Record<string, Record<string, CacheEntry<any>>>;

type GetCachedOptions = {
  keepUntil?: number; // forcibly remove the entry after this time (ms)
  invalidateCache?: boolean;
};

export function getCached<T>(
  facadeCache: FacadeCache,
  namespace: string,
  key: string,
  fetchFn: () => Promise<T>,
  options?: GetCachedOptions,
): Promise<T> {
  const keepUntil = options?.keepUntil ?? 5 * 60 * 1000; // 5 mins default

  if (!facadeCache[namespace]) {
    facadeCache[namespace] = {};
  }

  let entry = facadeCache[namespace][key];

  // Use existing cached data if available and fresh
  if (entry?.data !== undefined && !options?.invalidateCache) {
    return Promise.resolve(entry.data);
  }

  // Use in-flight request if present
  if (entry?.request) {
    return entry.request;
  }

  // Initialize cache entry if it doesn't exist
  if (!entry) {
    entry = { request: undefined, data: undefined };
    facadeCache[namespace][key] = entry;
  }

  // Start the request and store it in the cache
  const request = fetchFn()
    .then((result) => {
      entry!.data = result;
      return result;
    })
    .catch((err) => {
      // Optionally: clean up if the request fails
      delete facadeCache[namespace][key];
      throw err;
    })
    .finally(() => {
      entry!.request = undefined;

      // Set keepUntil expiration timer
      if (keepUntil !== 0) {
        // Clear previous timeout if any
        if (entry!.timeoutId) {
          clearTimeout(entry!.timeoutId);
        }

        entry!.timeoutId = setTimeout(() => {
          delete facadeCache[namespace][key];
        }, keepUntil);
      }
    });

  entry.request = request;

  return request;
}
